import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

const MetaballCanvas = () => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const materialRef = useRef(null);
  const clockRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const targetMouseRef = useRef({ x: 0.5, y: 0.5 });
  const cursorSphere3DRef = useRef(new THREE.Vector3(0, 0, 0));
  const activeMergesRef = useRef(0);
  const animationFrameRef = useRef(null);
  const [fps, setFps] = useState(0);
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isLowPowerDevice = isMobile || navigator.hardwareConcurrency <= 4;
  const devicePixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);

  const settings = {
    sphereCount: isMobile ? 3 : 6,
    fixedTopLeftRadius: isMobile ? 0.3 : 0.8,
    fixedBottomRightRadius: isMobile ? 0.35 : 0.9,
    smallTopLeftRadius: isMobile ? 0.15 : 0.3,
    smallBottomRightRadius: isMobile ? 0.18 : 0.35,
    cursorRadiusMin: isMobile ? 0.04 : 0.08,
    cursorRadiusMax: isMobile ? 0.08 : 0.15,
    animationSpeed: isMobile ? 0.4 : 0.6,
    movementScale: isMobile ? 0.6 : 1.2,
    mouseSmoothness: 0.1,
    mergeDistance: isMobile ? 1.0 : 1.5,
    mouseProximityEffect: !isMobile,
    minMovementScale: isMobile ? 0.2 : 0.3,
    maxMovementScale: isMobile ? 0.5 : 1.0,
    ambientIntensity: 0.12,
    diffuseIntensity: 1.2,
    specularIntensity: 2.5,
    specularPower: 3,
    fresnelPower: 0.8,
    backgroundColor: new THREE.Color(0x0a0a15),
    sphereColor: new THREE.Color(0x050510),
    lightColor: new THREE.Color(0xccaaff),
    lightPosition: new THREE.Vector3(0.9, 0.9, 1.2),
    smoothness: isMobile ? 0.5 : 0.8,
    contrast: 1.6,
    fogDensity: 0.06,
    cursorGlowIntensity: isMobile ? 0.6 : 1.2,
    cursorGlowRadius: isMobile ? 1.2 : 2.2,
    cursorGlowColor: new THREE.Color(0xaa77ff)
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const init = () => {
      const scene = new THREE.Scene();
      sceneRef.current = scene;

      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
      camera.position.z = 1;
      cameraRef.current = camera;

      clockRef.current = new THREE.Clock();

      const renderer = new THREE.WebGLRenderer({
        antialias: !isMobile && !isLowPowerDevice,
        alpha: true,
        powerPreference: isMobile ? 'default' : 'high-performance',
        preserveDrawingBuffer: false
      });

      const pixelRatio = Math.min(devicePixelRatio, isMobile ? 1.5 : 2);
      renderer.setPixelRatio(pixelRatio);

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      renderer.setSize(viewportWidth, viewportHeight);
      renderer.setClearColor(0x000000, 0);
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const canvas = renderer.domElement;
      canvas.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 0 !important;
        display: block !important;
      `;

      containerRef.current.appendChild(canvas);
      rendererRef.current = renderer;

      const material = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uResolution: { value: new THREE.Vector2(viewportWidth, viewportHeight) },
          uActualResolution: { value: new THREE.Vector2(viewportWidth * pixelRatio, viewportHeight * pixelRatio) },
          uPixelRatio: { value: pixelRatio },
          uMousePosition: { value: new THREE.Vector2(0.5, 0.5) },
          uCursorSphere: { value: new THREE.Vector3(0, 0, 0) },
          uCursorRadius: { value: settings.cursorRadiusMin },
          uSphereCount: { value: settings.sphereCount },
          uFixedTopLeftRadius: { value: settings.fixedTopLeftRadius },
          uFixedBottomRightRadius: { value: settings.fixedBottomRightRadius },
          uSmallTopLeftRadius: { value: settings.smallTopLeftRadius },
          uSmallBottomRightRadius: { value: settings.smallBottomRightRadius },
          uMergeDistance: { value: settings.mergeDistance },
          uSmoothness: { value: settings.smoothness },
          uAmbientIntensity: { value: settings.ambientIntensity },
          uDiffuseIntensity: { value: settings.diffuseIntensity },
          uSpecularIntensity: { value: settings.specularIntensity },
          uSpecularPower: { value: settings.specularPower },
          uFresnelPower: { value: settings.fresnelPower },
          uBackgroundColor: { value: settings.backgroundColor },
          uSphereColor: { value: settings.sphereColor },
          uLightColor: { value: settings.lightColor },
          uLightPosition: { value: settings.lightPosition },
          uContrast: { value: settings.contrast },
          uFogDensity: { value: settings.fogDensity },
          uAnimationSpeed: { value: settings.animationSpeed },
          uMovementScale: { value: settings.movementScale },
          uMouseProximityEffect: { value: settings.mouseProximityEffect },
          uMinMovementScale: { value: settings.minMovementScale },
          uMaxMovementScale: { value: settings.maxMovementScale },
          uCursorGlowIntensity: { value: settings.cursorGlowIntensity },
          uCursorGlowRadius: { value: settings.cursorGlowRadius },
          uCursorGlowColor: { value: settings.cursorGlowColor },
          uIsSafari: { value: isSafari ? 1.0 : 0.0 },
          uIsMobile: { value: isMobile ? 1.0 : 0.0 },
          uIsLowPower: { value: isLowPowerDevice ? 1.0 : 0.0 }
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          ${isMobile || isSafari || isLowPowerDevice ? 'precision mediump float;' : 'precision highp float;'}
          
          uniform float uTime;
          uniform vec2 uResolution;
          uniform vec2 uActualResolution;
          uniform float uPixelRatio;
          uniform vec2 uMousePosition;
          uniform vec3 uCursorSphere;
          uniform float uCursorRadius;
          uniform int uSphereCount;
          uniform float uFixedTopLeftRadius;
          uniform float uFixedBottomRightRadius;
          uniform float uSmallTopLeftRadius;
          uniform float uSmallBottomRightRadius;
          uniform float uMergeDistance;
          uniform float uSmoothness;
          uniform float uAmbientIntensity;
          uniform float uDiffuseIntensity;
          uniform float uSpecularIntensity;
          uniform float uSpecularPower;
          uniform float uFresnelPower;
          uniform vec3 uBackgroundColor;
          uniform vec3 uSphereColor;
          uniform vec3 uLightColor;
          uniform vec3 uLightPosition;
          uniform float uContrast;
          uniform float uFogDensity;
          uniform float uAnimationSpeed;
          uniform float uMovementScale;
          uniform bool uMouseProximityEffect;
          uniform float uMinMovementScale;
          uniform float uMaxMovementScale;
          uniform float uCursorGlowIntensity;
          uniform float uCursorGlowRadius;
          uniform vec3 uCursorGlowColor;
          uniform float uIsSafari;
          uniform float uIsMobile;
          uniform float uIsLowPower;
          
          varying vec2 vUv;
          
          const float PI = 3.14159265359;
          const float EPSILON = 0.001;
          const float MAX_DIST = 100.0;
          
          float smin(float a, float b, float k) {
            float h = max(k - abs(a - b), 0.0) / k;
            return min(a, b) - h * h * k * 0.25;
          }
          
          float sdSphere(vec3 p, float r) {
            return length(p) - r;
          }
          
          vec3 screenToWorld(vec2 normalizedPos) {
            vec2 uv = normalizedPos * 2.0 - 1.0;
            uv.x *= uResolution.x / uResolution.y;
            return vec3(uv * 2.0, 0.0);
          }
          
          float getDistanceToCenter(vec2 pos) {
            float dist = length(pos - vec2(0.5, 0.5)) * 2.0;
            return smoothstep(0.0, 1.0, dist);
          }
          
          float sceneSDF(vec3 pos) {
            float result = MAX_DIST;
            
            // Adjust positions based on device
            float topLeftX = uIsMobile > 0.5 ? 0.05 : 0.08;
            float topLeftY = uIsMobile > 0.5 ? 0.95 : 0.92;
            float smallTopLeftX = uIsMobile > 0.5 ? 0.18 : 0.25;
            float smallTopLeftY = uIsMobile > 0.5 ? 0.80 : 0.72;
            float bottomRightX = uIsMobile > 0.5 ? 0.95 : 0.92;
            float bottomRightY = uIsMobile > 0.5 ? 0.05 : 0.08;
            float smallBottomRightX = uIsMobile > 0.5 ? 0.82 : 0.72;
            float smallBottomRightY = uIsMobile > 0.5 ? 0.20 : 0.25;
            
            vec3 topLeftPos = screenToWorld(vec2(topLeftX, topLeftY));
            float topLeft = sdSphere(pos - topLeftPos, uFixedTopLeftRadius);
            
            vec3 smallTopLeftPos = screenToWorld(vec2(smallTopLeftX, smallTopLeftY));
            float smallTopLeft = sdSphere(pos - smallTopLeftPos, uSmallTopLeftRadius);
            
            vec3 bottomRightPos = screenToWorld(vec2(bottomRightX, bottomRightY));
            float bottomRight = sdSphere(pos - bottomRightPos, uFixedBottomRightRadius);
            
            vec3 smallBottomRightPos = screenToWorld(vec2(smallBottomRightX, smallBottomRightY));
            float smallBottomRight = sdSphere(pos - smallBottomRightPos, uSmallBottomRightRadius);
            
            float t = uTime * uAnimationSpeed;
            
            float dynamicMovementScale = uMovementScale;
            if (uMouseProximityEffect) {
              float distToCenter = getDistanceToCenter(uMousePosition);
              float mixFactor = smoothstep(0.0, 1.0, distToCenter);
              dynamicMovementScale = mix(uMinMovementScale, uMaxMovementScale, mixFactor);
            }
            
            int maxIter = uIsMobile > 0.5 ? 4 : (uIsLowPower > 0.5 ? 6 : min(uSphereCount, 10));
            for (int i = 0; i < 10; i++) {
              if (i >= uSphereCount || i >= maxIter) break;
              
              float fi = float(i);
              float speed = 0.4 + fi * 0.12;
              float radius = 0.12 + mod(fi, 3.0) * 0.06;
              float orbitRadius = (0.3 + mod(fi, 3.0) * 0.15) * dynamicMovementScale;
              float phaseOffset = fi * PI * 0.35;
              
              float distToCursor = length(vec3(0.0) - uCursorSphere);
              float proximityScale = 1.0 + (1.0 - smoothstep(0.0, 1.0, distToCursor)) * 0.5;
              orbitRadius *= proximityScale;
              
              vec3 offset;
              if (i == 0) {
                offset = vec3(
                  sin(t * speed) * orbitRadius * 0.7,
                  sin(t * 0.5) * orbitRadius,
                  cos(t * speed * 0.7) * orbitRadius * 0.5
                );
              } else if (i == 1) {
                offset = vec3(
                  sin(t * speed + PI) * orbitRadius * 0.5,
                  -sin(t * 0.5) * orbitRadius,
                  cos(t * speed * 0.7 + PI) * orbitRadius * 0.5
                );
              } else {
                offset = vec3(
                  sin(t * speed + phaseOffset) * orbitRadius * 0.8,
                  cos(t * speed * 0.85 + phaseOffset * 1.3) * orbitRadius * 0.6,
                  sin(t * speed * 0.5 + phaseOffset) * 0.3
                );
              }
              
              vec3 toCursor = uCursorSphere - offset;
              float cursorDist = length(toCursor);
              if (cursorDist < uMergeDistance && cursorDist > 0.0) {
                float attraction = (1.0 - cursorDist / uMergeDistance) * 0.3;
                offset += normalize(toCursor) * attraction;
              }
              
              float movingSphere = sdSphere(pos - offset, radius);
              
              float blend = 0.05;
              if (cursorDist < uMergeDistance) {
                float influence = 1.0 - (cursorDist / uMergeDistance);
                blend = mix(0.05, uSmoothness, influence * influence * influence);
              }
              
              result = smin(result, movingSphere, blend);
            }
            
            float cursorBall = sdSphere(pos - uCursorSphere, uCursorRadius);
            
            float topLeftGroup = smin(topLeft, smallTopLeft, 0.4);
            float bottomRightGroup = smin(bottomRight, smallBottomRight, 0.4);
            
            result = smin(result, topLeftGroup, 0.3);
            result = smin(result, bottomRightGroup, 0.3);
            result = smin(result, cursorBall, uSmoothness);
            
            return result;
          }
          
          vec3 calcNormal(vec3 p) {
            float eps = uIsLowPower > 0.5 ? 0.002 : 0.001;
            return normalize(vec3(
              sceneSDF(p + vec3(eps, 0, 0)) - sceneSDF(p - vec3(eps, 0, 0)),
              sceneSDF(p + vec3(0, eps, 0)) - sceneSDF(p - vec3(0, eps, 0)),
              sceneSDF(p + vec3(0, 0, eps)) - sceneSDF(p - vec3(0, 0, eps))
            ));
          }
          
          float ambientOcclusion(vec3 p, vec3 n) {
            if (uIsLowPower > 0.5) {
              float h1 = sceneSDF(p + n * 0.03);
              float h2 = sceneSDF(p + n * 0.06);
              float occ = (0.03 - h1) + (0.06 - h2) * 0.5;
              return clamp(1.0 - occ * 2.0, 0.0, 1.0);
            } else {
              float occ = 0.0;
              float weight = 1.0;
              for (int i = 0; i < 6; i++) {
                float dist = 0.01 + 0.015 * float(i * i);
                float h = sceneSDF(p + n * dist);
                occ += (dist - h) * weight;
                weight *= 0.85;
              }
              return clamp(1.0 - occ, 0.0, 1.0);
            }
          }
          
          float softShadow(vec3 ro, vec3 rd, float mint, float maxt, float k) {
            if (uIsLowPower > 0.5) {
              float result = 1.0;
              float t = mint;
              for (int i = 0; i < 3; i++) {
                t += 0.3;
                if (t >= maxt) break;
                float h = sceneSDF(ro + rd * t);
                if (h < EPSILON) return 0.0;
                result = min(result, k * h / t);
              }
              return result;
            } else {
              float result = 1.0;
              float t = mint;
              for (int i = 0; i < 20; i++) {
                if (t >= maxt) break;
                float h = sceneSDF(ro + rd * t);
                if (h < EPSILON) return 0.0;
                result = min(result, k * h / t);
                t += h;
              }
              return result;
            }
          }
          
          float rayMarch(vec3 ro, vec3 rd) {
            float t = 0.0;
            int maxSteps = uIsMobile > 0.5 ? 16 : (uIsSafari > 0.5 ? 16 : 48);
            
            for (int i = 0; i < 48; i++) {
              if (i >= maxSteps) break;
              
              vec3 p = ro + rd * t;
              float d = sceneSDF(p);
              
              if (d < EPSILON) {
                return t;
              }
              
              if (t > 5.0) {
                break;
              }
              
              t += d * (uIsLowPower > 0.5 ? 1.2 : 0.9);
            }
            
            return -1.0;
          }
          
          vec3 lighting(vec3 p, vec3 rd, float t) {
            if (t < 0.0) {
              return vec3(0.0);
            }
            
            vec3 normal = calcNormal(p);
            vec3 viewDir = -rd;
            
            vec3 baseColor = uSphereColor;
            
            float ao = ambientOcclusion(p, normal);
            
            vec3 ambient = uLightColor * uAmbientIntensity * ao;
            
            vec3 lightDir = normalize(uLightPosition);
            float diff = max(dot(normal, lightDir), 0.0);
            
            float shadow = softShadow(p, lightDir, 0.01, 10.0, 20.0);
            
            vec3 diffuse = uLightColor * diff * uDiffuseIntensity * shadow;
            
            vec3 reflectDir = reflect(-lightDir, normal);
            float spec = pow(max(dot(viewDir, reflectDir), 0.0), uSpecularPower);
            float fresnel = pow(1.0 - max(dot(viewDir, normal), 0.0), uFresnelPower);
            
            vec3 specular = uLightColor * spec * uSpecularIntensity * fresnel;
            
            vec3 fresnelRim = uLightColor * fresnel * 0.4;
            
            float distToCursor = length(p - uCursorSphere);
            if (distToCursor < uCursorRadius + 0.4) {
              float highlight = 1.0 - smoothstep(0.0, uCursorRadius + 0.4, distToCursor);
              specular += uLightColor * highlight * 0.2;
              
              float glow = exp(-distToCursor * 3.0) * 0.15;
              ambient += uLightColor * glow * 0.5;
            }
            
            vec3 color = (baseColor + ambient + diffuse + specular + fresnelRim) * ao;
            
            color = pow(color, vec3(uContrast * 0.9));
            color = color / (color + vec3(0.8));
            
            return color;
          }
          
          float calculateCursorGlow(vec3 worldPos) {
            float dist = length(worldPos.xy - uCursorSphere.xy);
            float glow = 1.0 - smoothstep(0.0, uCursorGlowRadius, dist);
            glow = pow(glow, 2.0);
            return glow * uCursorGlowIntensity;
          }
          
          void main() {
            vec2 uv = (gl_FragCoord.xy * 2.0 - uActualResolution.xy) / uActualResolution.xy;
            uv.x *= uResolution.x / uResolution.y;
            
            vec3 ro = vec3(uv * 2.0, -1.0);
            vec3 rd = vec3(0.0, 0.0, 1.0);
            
            float t = rayMarch(ro, rd);
            
            vec3 p = ro + rd * t;
            
            vec3 color = lighting(p, rd, t);
            
            float cursorGlow = calculateCursorGlow(ro);
            vec3 glowContribution = uCursorGlowColor * cursorGlow;
            
            if (t > 0.0) {
              float fogAmount = 1.0 - exp(-t * uFogDensity);
              color = mix(color, uBackgroundColor.rgb, fogAmount * 0.3);
              
              color += glowContribution * 0.3;
              
              gl_FragColor = vec4(color, 1.0);
            } else {
              if (cursorGlow > 0.01) {
                gl_FragColor = vec4(glowContribution, cursorGlow * 0.8);
              } else {
                gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
              }
            }
          }
        `,
        transparent: true
      });

      materialRef.current = material;

      const geometry = new THREE.PlaneGeometry(2, 2);
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);
    };

    const screenToWorldJS = (normalizedX, normalizedY) => {
      const uv_x = normalizedX * 2.0 - 1.0;
      const uv_y = normalizedY * 2.0 - 1.0;
      const aspect = window.innerWidth / window.innerHeight;
      return new THREE.Vector3(uv_x * aspect * 2.0, uv_y * 2.0, 0.0);
    };

    const onPointerMove = (event) => {
      targetMouseRef.current.x = event.clientX / window.innerWidth;
      targetMouseRef.current.y = 1.0 - event.clientY / window.innerHeight;

      const worldPos = screenToWorldJS(targetMouseRef.current.x, targetMouseRef.current.y);
      cursorSphere3DRef.current.copy(worldPos);

      let closestDistance = 1000.0;
      let merges = 0;

      // Adjust positions based on device
      const topLeftX = isMobile ? 0.05 : 0.08;
      const topLeftY = isMobile ? 0.95 : 0.92;
      const smallTopLeftX = isMobile ? 0.18 : 0.25;
      const smallTopLeftY = isMobile ? 0.80 : 0.72;
      const bottomRightX = isMobile ? 0.95 : 0.92;
      const bottomRightY = isMobile ? 0.05 : 0.08;
      const smallBottomRightX = isMobile ? 0.82 : 0.72;
      const smallBottomRightY = isMobile ? 0.20 : 0.25;
      
      const fixedPositions = [
        screenToWorldJS(topLeftX, topLeftY),
        screenToWorldJS(smallTopLeftX, smallTopLeftY),
        screenToWorldJS(bottomRightX, bottomRightY),
        screenToWorldJS(smallBottomRightX, smallBottomRightY)
      ];

      fixedPositions.forEach((pos) => {
        const dist = cursorSphere3DRef.current.distanceTo(pos);
        closestDistance = Math.min(closestDistance, dist);
        if (dist < settings.mergeDistance) merges++;
      });

      activeMergesRef.current = merges;

      const proximityFactor = Math.max(0, 1.0 - closestDistance / settings.mergeDistance);
      const smoothFactor = proximityFactor * proximityFactor * (3.0 - 2.0 * proximityFactor);
      const dynamicRadius = settings.cursorRadiusMin + (settings.cursorRadiusMax - settings.cursorRadiusMin) * smoothFactor;

      if (materialRef.current) {
        materialRef.current.uniforms.uCursorSphere.value.copy(cursorSphere3DRef.current);
        materialRef.current.uniforms.uCursorRadius.value = dynamicRadius;
      }
    };

    const onWindowResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const currentPixelRatio = Math.min(devicePixelRatio, isMobile ? 1.5 : 2);

      if (cameraRef.current) {
        cameraRef.current.updateProjectionMatrix();
      }

      if (rendererRef.current) {
        rendererRef.current.setSize(width, height);
        rendererRef.current.setPixelRatio(currentPixelRatio);
        
        // Update canvas style for mobile
        const canvas = rendererRef.current.domElement;
        canvas.style.cssText = `
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 0 !important;
          display: block !important;
        `;
      }

      if (materialRef.current) {
        materialRef.current.uniforms.uResolution.value.set(width, height);
        materialRef.current.uniforms.uActualResolution.value.set(
          width * currentPixelRatio,
          height * currentPixelRatio
        );
        materialRef.current.uniforms.uPixelRatio.value = currentPixelRatio;
      }
    };

    let frameCount = 0;
    let lastTime = performance.now();

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      const currentTime = performance.now();
      frameCount++;

      if (currentTime - lastTime >= 1000) {
        const currentFps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        setFps(currentFps);
        frameCount = 0;
        lastTime = currentTime;
      }

      mouseRef.current.x += (targetMouseRef.current.x - mouseRef.current.x) * settings.mouseSmoothness;
      mouseRef.current.y += (targetMouseRef.current.y - mouseRef.current.y) * settings.mouseSmoothness;

      if (materialRef.current && clockRef.current) {
        materialRef.current.uniforms.uTime.value = clockRef.current.getElapsedTime();
        materialRef.current.uniforms.uMousePosition.value.set(mouseRef.current.x, mouseRef.current.y);
      }

      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };

    const onTouchMove = (event) => {
      if (event.touches.length > 0) {
        const touch = event.touches[0];
        onPointerMove({
          clientX: touch.clientX,
          clientY: touch.clientY
        });
      }
    };

    init();
    animate();

    // Mouse events for desktop
    window.addEventListener('mousemove', onPointerMove, { passive: true });
    
    // Touch events for mobile/tablet
    window.addEventListener('touchstart', onTouchMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    
    window.addEventListener('resize', onWindowResize, { passive: true });
    window.addEventListener('orientationchange', () => {
      setTimeout(onWindowResize, 100);
    }, { passive: true });

    // Initial position
    onPointerMove({ clientX: window.innerWidth / 2, clientY: window.innerHeight / 2 });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('touchstart', onTouchMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('resize', onWindowResize);
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        width: '100vw', 
        height: '100vh', 
        zIndex: 0,
        overflow: 'hidden',
        touchAction: 'none'
      }} 
    />
  );
};

export default MetaballCanvas;
